// This file combineds breed and color selection into a single screen
// instead of handling them across multiple screens in the original flow
// the fields are rendered conditionally based on the locationShowCreditCardStep prop

import { useState, useEffect } from 'react';

import styled from '@emotion/styled';
import {
    colors, maximumAge, minimumAge, approximateAgeToBirthdate,
    birthdateToApproximateAge,
} from '@modernanimal/common';
import { ColorType } from '@modernanimal/common/src/__generated__/graphql';
import { BreedOptionType, formatBreedOptions, filterSearchValues } from '@modernanimal/common/src/utils/patientOnboarding';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { useBreakpoint } from 'use-breakpoint';

import {
    CustomerPatientSpeciesEnum,
    CustomerPatientSexEnum,
    CustomerBreedSpeciesEnum,
    CustomerColorSpeciesEnum,
    GetColorsQuery
} from '../__generated__/graphql';
import PromoCodeInput from '../clientOnboarding/PromoCodeInput';
import InputField from '../components/InputField';
import PrimaryButton from '../components/PrimaryButton';
import SearchInputField from '../components/SearchInputField';
import {
    BREED_NOT_PROVIDED_CAT,
    BREED_NOT_PROVIDED_DOG,
    COLOR_NOT_PROVIDED_CAT,
    COLOR_NOT_PROVIDED_DOG
} from '../constants';
import Fonts from '../fonts';
import { DOWNLOAD_APP_LINK, HREF_TARGET, BREAKPOINTS } from '../helpers';
import Icons from '../icons';
import images from '../images';
import { getBreeds, getColors } from '../patientOnboarding/patientOnboarding.helpers';
import useWebBookingStore, { PatientStoreType } from '../store';
import { isNumeric } from '../utils';

type Props = {
    goToNextStep: ()=> void;
    locationShowCreditCardStep: boolean;
};

// TODO reduce complexity - https://modernanimal.atlassian.net/browse/CLAUDE-2235
// eslint-disable-next-line complexity
const TestPatientBasics = ({ goToNextStep, locationShowCreditCardStep }: Props) => {
    const {
        patient, updatePatient
    } = useWebBookingStore((state) => ({
        patient: state.patient,
        updatePatient: state.setPatient,
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    const [patientName, setPatientName] = useState(patient.name);
    const [patientSpecies, setPatientSpecies] = useState<CustomerPatientSpeciesEnum | null>(patient.species);
    const [isBirthdateKnown, setIsBirthdateKnown] = useState<boolean | null>(patient.isBirthdateKnown);
    const [birthdate, setBirthdate] = useState(patient.isBirthdateKnown ? patient.birthdate : '');
    const [approximateAge, setApproximateAge] = useState<{ years: string, months: string }>(
        patient.isBirthdateKnown === false
            ? birthdateToApproximateAge(patient.birthdate)
            : { years: '', months: '' }
    );
    const [isValidBirthdate, setIsValidBirthdate] = useState(true);

    // Sex, Breed and Color state
    const [breed, setBreed] = useState<BreedOptionType>(patient.breed);
    const [breedOptions, setBreedOptions] = useState<BreedOptionType[]>([]);
    const [isBreedValid, setIsBreedValid] = useState(true);
    const [otherBreedSearchResult, setOtherBreedSearchResult] = useState<BreedOptionType | undefined>(undefined);
    const [color, setColor] = useState<GetColorsQuery['colors'][0]>(patient.color);
    const [colorOptions, setColorOptions] = useState<GetColorsQuery['colors']>([]);
    const [isColorValid, setIsColorValid] = useState(true);
    const [sex, setSex] = useState<CustomerPatientSexEnum | 'Unknown' | null>(
        // If patient.sex is null, we can determine if it's Unknown or not selected by checking
        // if the breed has been filled out already. If it has, we know the user has selected Unknown.
        patient.sex ?? (patient.breed.id ? 'Unknown' : null)
    );
    const [spayedOrNeutered, setSpayedOrNeutered] = useState<boolean | null>(patient.spayedOrNeutered);

    const isBirthdateEntered = (isBirthdateKnown && !!birthdate && isValidBirthdate)
        || (isBirthdateKnown === false && !!(approximateAge.years || approximateAge.months));

    // Modify validation logic to consider locationShowCreditCardStep
    const isValidInfo = !!patientName && !!patientSpecies && isBirthdateEntered && sex !== null && !!breed.id
        // Only check color if locationShowCreditCardStep is true
        && (locationShowCreditCardStep ? !!color.id : true);

    // Promo code
    const [isPromoSectionOpen, setIsPromoSectionOpen] = useState(false);
    const [params] = useSearchParams();
    const promoCodeFromUrl = params.get('promoCode');

    useEffect(() => {
        if (promoCodeFromUrl) {
            setIsPromoSectionOpen(true);
        }
    }, [promoCodeFromUrl]);

    useEffect(() => {
        if (birthdate) {
            setApproximateAge({ years: '', months: '' });
        }
    }, [birthdate]);

    useEffect(() => {
        if (approximateAge.years || approximateAge.months) {
            setIsValidBirthdate(true);
            setBirthdate('');
        }
    }, [approximateAge]);

    // Fetch breeds and colors when species changes
    useEffect(() => {
        const fetchBreedsAndColors = async () => {
            if (!patientSpecies) return;

            const breeds = await getBreeds(patientSpecies as unknown as CustomerBreedSpeciesEnum);
            const formattedBreedOptions = formatBreedOptions(breeds);
            setBreedOptions(formattedBreedOptions);
            const otherBreed = formattedBreedOptions.find((breedOption) => breedOption.name === 'Other');
            if (otherBreed) setOtherBreedSearchResult(otherBreed);

            const colors = await getColors(patientSpecies as unknown as CustomerColorSpeciesEnum);
            setColorOptions(colors);
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchBreedsAndColors();
    }, [patientSpecies]);

    const changeBirthdate = (birthdate: string) => {
        const luxonBirthdate = DateTime.fromFormat(birthdate, 'yyyy-MM-dd').toISO();
        const minBirthdate = DateTime.fromJSDate(minimumAge).toISO();
        const maxBirthdate = DateTime.fromJSDate(maximumAge).toISO();
        const isValid = luxonBirthdate <= maxBirthdate && luxonBirthdate >= minBirthdate;

        setIsValidBirthdate(isValid);
        setBirthdate(birthdate);
    };

    const handleNext = () => {
        if (!patientSpecies) return;

        // Prepare the breed and color data
        const breedColorData = getBreedAndColorData(patientSpecies, breed, color);

        // Handle species change by clearing breed/color if needed
        const speciesChangeAttributes = handleSpeciesChange(patient, patientSpecies);

        // Process birthdate from known date or approximate age
        const exactBirthdate = isBirthdateKnown
            ? birthdate
            : approximateAgeToBirthdate(+approximateAge.years, +approximateAge.months);

        // Normalize sex value (Unknown -> null)
        const defaultSex = sex === 'Unknown' ? null : sex;

        // Update patient data and proceed
        updatePatient({
            ...patient,
            name: patientName,
            species: patientSpecies,
            birthdate: exactBirthdate,
            isBirthdateKnown,
            ...breedColorData,
            sex: defaultSex,
            spayedOrNeutered,
            ...speciesChangeAttributes
        });

        goToNextStep();
    };

    // Helper function to get appropriate breed and color based on settings
    const getBreedAndColorData = (
        species: CustomerPatientSpeciesEnum,
        currentBreed: BreedOptionType,
        currentColor: ColorType
    ) => {
        const defaultColorId = species === CustomerPatientSpeciesEnum.Dog
            ? COLOR_NOT_PROVIDED_DOG
            : COLOR_NOT_PROVIDED_CAT;

        // Use selected values when credit card step is shown
        if (locationShowCreditCardStep) {
            return {
                breed: currentBreed,
                color: currentColor
            };
        }

        // Use default values when credit card step is not shown
        return {
            breed: currentBreed,
            color: {
                id: defaultColorId,
                name: 'NOT PROVIDED'
            }
        };
    };

    // Helper function to handle species change
    const handleSpeciesChange = (currentPatient: PatientStoreType, newSpecies: CustomerPatientSpeciesEnum) => {
        const speciesChanged = currentPatient.species
                               && newSpecies !== currentPatient.species
                               && currentPatient.breed.id
                               && currentPatient.color.id;

        if (!speciesChanged || !locationShowCreditCardStep) {
            return {};
        }

        // Clear breed and color when species changes
        return {
            breed: {
                id: '',
                name: '',
                isMixed: false
            },
            color: {
                id: '',
                name: ''
            }
        };
    };

    const selectBirthdateIsKnown = (isKnown: boolean) => setIsBirthdateKnown(
        isBirthdateKnown === isKnown ? null : isKnown
    );

    const selectSpecies = (species: CustomerPatientSpeciesEnum) => (
        setPatientSpecies(patientSpecies === species ? null : species)
    );

    // Sex selection functions
    const selectSex = (patientSex: CustomerPatientSexEnum | 'Unknown') => {
        setSex(sex !== patientSex ? patientSex : null);
        setSpayedOrNeutered(null);
    };

    const selectSpayedOrNeutered = (isSpayedOrNeutered: boolean) => setSpayedOrNeutered(
        spayedOrNeutered === isSpayedOrNeutered ? null : isSpayedOrNeutered
    );

    const renderBirthdateSelections = () => {
        const yesIsSelected = isBirthdateKnown === true;
        const noIsSelected = isBirthdateKnown === false;
        const YesFont = yesIsSelected ? Fonts.LabelMedium : Fonts.Body;
        const NoFont = noIsSelected ? Fonts.LabelMedium : Fonts.Body;
        return (
            <BirthdateSelectionContainer>
                <BirthdateSelection isSelected={yesIsSelected} onClick={() => selectBirthdateIsKnown(true)}>
                    <YesFont style={{ color: yesIsSelected ? colors.GREY_1 : colors.GREY_3 }}>
                        Yes
                    </YesFont>
                </BirthdateSelection>
                <BirthdateSelection isSelected={noIsSelected} onClick={() => selectBirthdateIsKnown(false)}>
                    <NoFont style={{ color: noIsSelected ? colors.GREY_1 : colors.GREY_3 }}>
                        No
                    </NoFont>
                </BirthdateSelection>
            </BirthdateSelectionContainer>
        );
    };

    const renderApproximateAgeInput = () => (
        <Subbody>
            <Fonts.LabelMedium>Approximate age</Fonts.LabelMedium>
            <ApproximateAgeContainer>
                <Subbody style={{ flex: 1 }}>
                    <Fonts.LabelMedium>Years</Fonts.LabelMedium>
                    <InputField
                        placeholder="Enter Years"
                        value={approximateAge.years}
                        onChange={(years) => {
                            if (years === '' || isNumeric(years)) {
                                setApproximateAge((prevApproximateAge) => ({
                                    ...prevApproximateAge,
                                    years
                                }));
                            }
                        }}
                        maxLength={2}
                        inputMode="numeric"
                        autoFocus={!approximateAge.years && !approximateAge.months}
                    />
                </Subbody>
                <Subbody style={{ flex: 1 }}>
                    <Fonts.LabelMedium>Months</Fonts.LabelMedium>
                    <InputField
                        placeholder="Enter Months"
                        value={approximateAge.months}
                        onChange={(months) => {
                            if (months === '' || isNumeric(months)) {
                                setApproximateAge((prevApproximateAge) => ({
                                    ...prevApproximateAge,
                                    months
                                }));
                            }
                        }}
                        maxLength={2}
                        inputMode="numeric"
                    />
                </Subbody>
            </ApproximateAgeContainer>
        </Subbody>
    );

    const renderKnownBirthdateInput = () => (
        <Subbody>
            <Fonts.LabelMedium>{'What is your pet\'s date of birth?'}</Fonts.LabelMedium>
            <DateInputContainer>
                <InputField
                    type="date"
                    value={birthdate}
                    onChange={changeBirthdate}
                    isInvalid={!isValidBirthdate}
                    min={minimumAge.toLocaleDateString('en-ca')}
                    max={maximumAge.toLocaleDateString('en-ca')}
                    dataTestId="known-birthdate-input"
                    autoFocus={!!isBirthdateKnown && !birthdate}
                />
                <CalendarIcon>
                    <Icons.Calendar data-testid="calendarIcon" height={24} width={24} fill={colors.PRIMARY_PERIWINKLE} />
                </CalendarIcon>
            </DateInputContainer>
            {!isValidBirthdate && (
                <Fonts.BodySmall style={{ color: colors.RED }}>Birthdate is out of valid range</Fonts.BodySmall>
            )}
        </Subbody>
    );

    const renderSpeciesSelections = () => (
        [CustomerPatientSpeciesEnum.Dog, CustomerPatientSpeciesEnum.Cat].map((species) => {
            const speciesImage = species === CustomerPatientSpeciesEnum.Dog ? images.Dog : images.Cat;
            const name = species === CustomerPatientSpeciesEnum.Dog ? 'Dog' : 'Cat';
            const isSelected = species === patientSpecies;

            return (
                <SpeciesSelection data-testid={`${name}Option`} key={`${name}-selection`} onClick={() => selectSpecies(species)}>
                    <IconAndName>
                        <SpeciesIcon src={speciesImage} />
                        <Fonts.Body>{name}</Fonts.Body>
                    </IconAndName>
                    {isSelected ? (
                        <Icons.CircleCheck height={24} width={24} fill={colors.GREEN} />
                    ) : (
                        <EmptyCircle />
                    )}
                </SpeciesSelection>
            );
        })
    );

    // Breed input render function
    const renderBreedInput = () => (
        <Subbody>
            <Fonts.LabelMedium>Breed</Fonts.LabelMedium>
            <SearchInputField
                placeholder={!patientSpecies ? 'Select species first' : 'Choose breed'}
                selectedValue={breed}
                searchQuery={(searchValue) => {
                    const breedResults = searchValue !== '' ? filterSearchValues(searchValue, breedOptions, 15) : [];
                    const formattedBreedResults = breedResults
                        .filter((breedResult) => breedResult.id !== BREED_NOT_PROVIDED_DOG
                            && breedResult.id !== BREED_NOT_PROVIDED_CAT)
                        .map((breedResult) => ({
                            ...breedResult,
                            isMixed: !!breedResult.isMixed
                        }));
                    return formattedBreedResults;
                }}
                onSelectResult={(result) => setBreed({
                    id: result.id,
                    name: result.name,
                    isMixed: !!result.isMixed
                })}
                searchResultKey={(result) => `${result.name}${result.isMixed ? '-isMixed' : ''}`}
                searchResultName="name"
                autoFocus={!breed}
                otherSearchResult={otherBreedSearchResult}
                isSearchValueValid={isBreedValid}
                setIsSearchValueValid={setIsBreedValid}
                disabled={!patientSpecies}
            />
            <Fonts.Body style={{ color: colors.GREY_2 }}>
                {'If you don\'t know the breed, type "other."'}
            </Fonts.Body>
            {!isBreedValid && (
                <Fonts.Body style={{ color: colors.RED }}>
                    Please select from the dropdown.
                </Fonts.Body>
            )}
        </Subbody>
    );

    // Color input render function
    const renderColorInput = () => (
        <Subbody>
            <Fonts.LabelMedium>Color</Fonts.LabelMedium>
            <SearchInputField
                placeholder={!patientSpecies ? 'Select species first' : 'Predominant color'}
                selectedValue={color}
                searchQuery={(searchValue) => {
                    const colorResults = searchValue !== '' ? filterSearchValues(searchValue, colorOptions, 15) : [];
                    const filteredColorResults = colorResults.filter(
                        (colorResult) => colorResult.id !== COLOR_NOT_PROVIDED_DOG
                            && colorResult.id !== COLOR_NOT_PROVIDED_CAT
                    );
                    return filteredColorResults;
                }}
                onSelectResult={(result) => setColor({
                    id: result.id,
                    name: result.name
                })}
                searchResultKey="name"
                searchResultName="name"
                isSearchValueValid={isColorValid}
                setIsSearchValueValid={setIsColorValid}
                disabled={!patientSpecies}
            />
            {!isColorValid && (
                <Fonts.Body style={{ color: colors.RED }}>
                    Please select from the dropdown.
                </Fonts.Body>
            )}
        </Subbody>
    );

    // Sex selections render function
    const renderSexSelections = () => {
        const sexSelectionOptions: (CustomerPatientSexEnum | 'Unknown')[] = [CustomerPatientSexEnum.M, CustomerPatientSexEnum.F, 'Unknown'];
        const showSpayedOrNeutered = sex === CustomerPatientSexEnum.F || sex === CustomerPatientSexEnum.M;
        return (
            <>
                <SexSelectionRow>
                    {sexSelectionOptions.map((patientSex) => {
                        const isSelected = sex === patientSex;
                        const SexSelectionFont = isSelected ? Fonts.LabelMedium : Fonts.Body;
                        let sexSelectionText = 'Unknown';
                        if (patientSex === CustomerPatientSexEnum.M) sexSelectionText = 'Male';
                        else if (patientSex === CustomerPatientSexEnum.F) sexSelectionText = 'Female';

                        return (
                            <SexSelection
                                key={`patient-sex-${sexSelectionText}`}
                                isSelected={isSelected}
                                onClick={() => selectSex(patientSex)}
                            >
                                <SexSelectionFont style={{ color: isSelected ? colors.GREY_1 : colors.GREY_3 }}>
                                    {sexSelectionText}
                                </SexSelectionFont>
                            </SexSelection>

                        );
                    })}
                </SexSelectionRow>
                {showSpayedOrNeutered && (
                    <SexSelectionRow>
                        {[true, false].map((isSpayedOrNeutered) => {
                            const spayedOrNeuteredText = sex === CustomerPatientSexEnum.F ? 'Spayed' : 'Neutered';
                            const isSelected = spayedOrNeutered === isSpayedOrNeutered;
                            const SpayedOrNeuteredFont = isSelected ? Fonts.LabelMedium : Fonts.Body;
                            return (
                                <SexSelection
                                    key={`${isSpayedOrNeutered ? '' : 'not-'}spayed-or-neutered`}
                                    isSelected={isSelected}
                                    onClick={() => selectSpayedOrNeutered(isSpayedOrNeutered)}
                                >
                                    <SpayedOrNeuteredFont style={{ color: isSelected ? colors.GREY_1 : colors.GREY_3 }}>
                                        {isSpayedOrNeutered ? spayedOrNeuteredText : `Not ${spayedOrNeuteredText}`}
                                    </SpayedOrNeuteredFont>
                                </SexSelection>
                            );
                        })}
                    </SexSelectionRow>
                )}
            </>
        );
    };

    const renderPromoSection = () => (
        <PromoCodeContainer isOpen={isPromoSectionOpen}>
            <PromoCodeHeaderButton
                onClick={() => setIsPromoSectionOpen(!isPromoSectionOpen)}
                isOpen={isPromoSectionOpen}
                data-testid="promo-code-header-button"
            >
                <Fonts.Body
                    style={{ color: isPromoSectionOpen ? colors.GREY_1 : colors.PRIMARY_PERIWINKLE }}
                >
                    Have a Promo Code?
                </Fonts.Body>
                <RotatingChevron isOpen={isPromoSectionOpen}>
                    <Icons.ChevronDown height={16} width={16} fill={colors.GREY_1} />
                </RotatingChevron>
            </PromoCodeHeaderButton>
            {isPromoSectionOpen && (
                <PromoCodeInput
                    promoCodeFromUrl={promoCodeFromUrl ?? ''}
                />
            )}
        </PromoCodeContainer>
    );

    return (
        <PatientBasicsContainer>
            <Body>
                <Instructions>
                    <Fonts.Heading04>
                        {'Let\'s get to know your Modern Animal'}
                    </Fonts.Heading04>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        {'If you need to book for multiple pets, please book and '}
                        <Link href={DOWNLOAD_APP_LINK} target={HREF_TARGET}>
                            download our app
                        </Link>
                        .
                    </Fonts.Body>
                </Instructions>
                <Subbody>
                    <Fonts.LabelMedium>Pet Name</Fonts.LabelMedium>
                    <InputField
                        value={patientName}
                        placeholder="Pet name"
                        autoFocus={!patientName}
                        onChange={setPatientName}
                    />
                </Subbody>
                <Subbody>
                    <Fonts.LabelMedium>Are they a dog or a cat?</Fonts.LabelMedium>
                    {renderSpeciesSelections()}
                </Subbody>
                <Subbody>
                    <Fonts.LabelMedium>{'Do you know your pet\'s date of birth?'}</Fonts.LabelMedium>
                    {renderBirthdateSelections()}
                </Subbody>
                {isBirthdateKnown && renderKnownBirthdateInput()}
                {isBirthdateKnown === false && renderApproximateAgeInput()}
                <Subbody>
                    <Fonts.LabelMedium>Sex</Fonts.LabelMedium>
                    {renderSexSelections()}
                </Subbody>

                {renderBreedInput()}

                {/* Only render color fields if locationShowCreditCardStep is true */}
                {locationShowCreditCardStep && renderColorInput()}

                {renderPromoSection()}
            </Body>
            <NextButtonContainer isMobile={isMobile}>
                <PrimaryButton id="patients-basic-button" disabled={!isValidInfo} onClick={handleNext} color="periwinkle">
                    Next
                </PrimaryButton>
            </NextButtonContainer>
        </PatientBasicsContainer>
    );
};

export default TestPatientBasics;

const PatientBasicsContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 32px 24px 0 24px;
    gap: 8px;
    justify-content: space-between;
`;

const ApproximateAgeContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
`;

const BirthdateSelectionContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
`;

const BirthdateSelection = styled.button<{ isSelected: boolean }>`
    display: flex;
    height: 48px;
    justify-content: center;
    align-items: center;
    flex: 1;
    border-radius: 40px;
    background-color: ${({ isSelected }) => (isSelected ? colors.WHITE : colors.GREY_6)};
    border: ${({ isSelected }) => `1px solid ${isSelected ? colors.PERIWINKLE_4 : colors.GREY_6}`};
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const CalendarIcon = styled.div`
    display: flex;
    position: absolute;
    right: 12px;
    pointer-events: none;
`;

const DateInputContainer = styled.div`
    display: flex;
    position: relative;
    align-items: center;
`;

const EmptyCircle = styled.div`
    display: flex;
    height: 22px;
    width: 22px;
    border-radius: 12px;
    border: 1px solid ${colors.PERIWINKLE_4};
`;

const IconAndName = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;
`;

const Instructions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const Link = styled.a`
    color: ${colors.PRIMARY_PERIWINKLE};
`;

const NextButtonContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const SpeciesIcon = styled.img`
    height: 28px;
    width: 28px;
`;

const SpeciesSelection = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid ${colors.PERIWINKLE_5};
    padding: 0 12px;
`;

const Subbody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const PromoCodeContainer = styled.div<{ isOpen: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    border: 1px solid ${colors.PERIWINKLE_4};
    border-left: none;
    border-right: none;

    ${({ isOpen }) => (isOpen && 'padding: 0 0 14px 0;')}
`;

const PromoCodeHeaderButton = styled.button<{ isOpen: boolean }>`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    align-items: center;

    // Using padding instead of gap to create space between header and input field to
    // allow for a larger click area
    padding: 14px 0 14px 0;
    ${({ isOpen }) => (isOpen && 'padding-bottom: 8px;')}
`;

const RotatingChevron = styled.div<{ isOpen: boolean }>`
    display: flex;
    transition: transform 0.3s ease-in-out;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(-180deg)' : 'rotate(0deg)')};
`;

// Additional styled components for sex selection
const SexSelection = styled.button<{ isSelected: boolean }>`
    display: flex;
    height: 48px;
    justify-content: center;
    align-items: center;
    flex: 1;
    border-radius: 40px;
    background-color: ${({ isSelected }) => (isSelected ? colors.WHITE : colors.GREY_6)};
    border: ${({ isSelected }) => `1px solid ${isSelected ? colors.PERIWINKLE_4 : colors.GREY_6}`};
`;

const SexSelectionRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
`;
